import type { TextMetrics } from "../types";

const WORD_PATTERN = /[\p{L}\p{N}]+(?:['-][\p{L}\p{N}]+)*/gu;
const SENTENCE_PATTERN = /[^.!?]+[.!?]+|[^.!?]+$/g;
const VOWEL_GROUP_PATTERN = /[aeiouy]+/gi;

export function analyzeText(input: string): TextMetrics {
  const trimmed = input.trim();

  if (!trimmed) {
    return {
      words: 0,
      characters: 0,
      sentences: 0,
      paragraphs: 0,
      readingTimeMinutes: 0,
      averageWordsPerSentence: 0,
      averageSyllablesPerWord: 0,
      readabilityLabel: "Easy"
    };
  }

  const words = trimmed.match(WORD_PATTERN) ?? [];
  const sentences = trimmed.match(SENTENCE_PATTERN)?.filter((sentence) => sentence.trim().length > 0) ?? [];
  const paragraphs = trimmed.split(/\n\s*\n/).filter((paragraph) => paragraph.trim().length > 0);
  const syllables = words.reduce((total, word) => total + countSyllables(word), 0);
  const averageWordsPerSentence = words.length / Math.max(sentences.length, 1);
  const averageSyllablesPerWord = syllables / Math.max(words.length, 1);

  return {
    words: words.length,
    characters: trimmed.length,
    sentences: sentences.length,
    paragraphs: paragraphs.length,
    readingTimeMinutes: Math.ceil(words.length / 225),
    averageWordsPerSentence: roundOne(averageWordsPerSentence),
    averageSyllablesPerWord: roundOne(averageSyllablesPerWord),
    readabilityLabel: estimateReadabilityLabel(words.length, sentences.length, averageSyllablesPerWord)
  };
}

export function estimateReadabilityLabel(
  words: number,
  sentences: number,
  averageSyllablesPerWord: number
): TextMetrics["readabilityLabel"] {
  const averageWordsPerSentence = words / Math.max(sentences, 1);
  const score = averageWordsPerSentence + averageSyllablesPerWord * 8;

  if (score < 18) {
    return "Easy";
  }

  if (score < 28) {
    return "Moderate";
  }

  return "Advanced";
}

function countSyllables(word: string): number {
  const normalized = word.toLowerCase().replace(/e\b/, "");
  const groups = normalized.match(VOWEL_GROUP_PATTERN);
  return Math.max(groups?.length ?? 1, 1);
}

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}
